import { apiErrorMessage } from '@fieldvision/web-core';

import { Button } from './Button.js';
import { Banner } from './primitives.js';

/**
 * A read that failed, said out loud.
 *
 * WHAT THIS REPLACES: several drill-down sections handled `isPending` and not
 * `error`, so a query that failed fell through to the same branch as a query
 * that returned nothing — "Tidak ada penilaian pada rentang ini." An outage and
 * an empty month looked identical, and the second reading is the dangerous one,
 * because a manager acts on "there were no visits" rather than reloading.
 *
 * `what` is the thing that did not load, lower-cased into the sentence
 * ("Gagal memuat penilaian."). The server's own message follows it when there is
 * one: it is often the useful half ("periode ini tidak terkunci"), and on a
 * dropped connection `apiErrorMessage` supplies the honest network sentence
 * instead of a status code.
 *
 * `onRetry` is optional but should almost always be `query.refetch`. The failure
 * this component mostly reports is an api restart, which resolves itself — and a
 * button beats telling somebody to reload the page and lose their filters.
 */
export function QueryError({
  what,
  error,
  onRetry,
}: {
  what: string;
  error?: unknown;
  onRetry?: () => void;
}) {
  const detail = apiErrorMessage(error);

  return (
    <Banner tone="error" live>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p>Gagal memuat {what}.</p>
          {detail === '' ? null : <p className="mt-0.5 text-sm opacity-90">{detail}</p>}
        </div>
        {onRetry ? (
          <Button size="sm" variant="secondary" onClick={onRetry}>
            Coba lagi
          </Button>
        ) : null}
      </div>
    </Banner>
  );
}
